import { AccountCircleOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import { CuriosityValue, CuriosityTranslation, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { useRestricted } from '@procureai/permissions-ui';
import { TFunction } from 'i18next';
import { ExternalLinkIcon } from 'lucide-react';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BaseDomainType, ModuleEntityType } from '~/common/types';
import { I18nNamespace } from '~/common/types/I18nNamespace';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { RouteId } from '~/routes/RouteId';

/**
 * Type alias for the supported entity types in the BaseEntityChip component.
 */
export type BaseEntityChipEntityTypes =
  | ModuleEntityType.CONTRACT
  | ModuleEntityType.ORDER
  | ModuleEntityType.PURCHASE_REQUISITION
  | ModuleEntityType.SOURCING_PROJECT
  | ModuleEntityType.SUPPLIER
  | ModuleEntityType.USER
  | CuriosityNamespaceType.FILE;

/**
 * Props for the BaseEntityChip component.
 *
 * @interface BaseEntityChipProps
 * @property {BaseEntityChipEntityTypes} entityType - The type of entity being displayed in the chip.
 * @property {string} uuid - The unique identifier for the entity.
 * @property {string} url - Optional external URL; renders the chip as an external link.
 * @property {string} name - Optional pre-resolved entity name; when provided, skips the Curiosity
 * lookup and uses this label directly. Useful for callers that already have the name in hand
 * (e.g., tool outputs) to avoid a stuck "loading…" fallback when Curiosity has no entry for the uuid.
 */
export interface BaseEntityChipProps {
  entityType: BaseEntityChipEntityTypes;
  uuid: string;
  url?: string;
  name?: string;
}

interface MappedEntityDetails {
  namespace?: CuriosityNamespaceType;
  permission?: Permission;
  routeId?: (uuid: string) => string;
  tooltipText?: string;
  icon?: ReactElement<any>;
}
const entityTypeMap: Record<BaseEntityChipEntityTypes, MappedEntityDetails> = {
  [ModuleEntityType.CONTRACT]: {
    namespace: CuriosityNamespaceType.CONTRACT,
    permission: Permission.BaseContractList,
    routeId: RouteId.contractDetail,
    icon: <DomainIcon domain={BaseDomainType.CONTRACT} />,
  },
  [ModuleEntityType.ORDER]: {
    namespace: CuriosityNamespaceType.PURCHASE_ORDER,
    permission: Permission.BaseOrderList,
    routeId: RouteId.orderDetail,
    icon: <DomainIcon domain={BaseDomainType.ORDER} />,
  },
  [ModuleEntityType.PURCHASE_REQUISITION]: {
    namespace: CuriosityNamespaceType.PURCHASE_REQUISITION,
    permission: Permission.BaseRequisitionList,
    routeId: RouteId.purchaseRequisitionDetail,
    icon: <DomainIcon domain={BaseDomainType.PURCHASE_REQUISITION} />,
  },
  [ModuleEntityType.SOURCING_PROJECT]: {
    namespace: CuriosityNamespaceType.SOURCING_PROJECT,
    permission: Permission.SearchIndexSourcingView,
    routeId: RouteId.sourcingProjectDetail,
    icon: <DomainIcon domain={BaseDomainType.SOURCING_PROJECT} />,
  },
  [ModuleEntityType.SUPPLIER]: {
    namespace: CuriosityNamespaceType.SUPPLIER,
    permission: Permission.BaseSupplierList,
    routeId: RouteId.supplierDetail,
    icon: <DomainIcon domain={BaseDomainType.SUPPLIER} />,
  },
  [ModuleEntityType.USER]: {
    // Not routing to profile page as it requires the internal user id
    // which is retrieved through conversations at this moment
    namespace: CuriosityNamespaceType.USER,
    icon: <AccountCircleOutlined />,
  },
  [CuriosityNamespaceType.FILE]: {
    namespace: CuriosityNamespaceType.FILE,
    icon: <InsertDriveFileOutlined />,
  },
};

/**
 * Maps entity details based on the entity type and UUID.
 *
 * @param entityType - The type of entity
 * @param t - The translation function
 *
 * @returns An object containing namespace, permission, routeId, tooltipText, and icon for the entity
 */
const mapEntityDetails = (
  entityType: BaseEntityChipEntityTypes,
  t: TFunction<I18nNamespace.intake, 'BaseEntityChip'>,
): MappedEntityDetails => {
  const entityDetails = entityTypeMap[entityType];

  if (entityDetails) {
    return {
      ...entityDetails,
      tooltipText: t(`tooltip.${entityType}`),
    };
  }

  // Fallback logic in case the entity type is not found in the map
  return {
    namespace: undefined,
    permission: undefined,
    routeId: undefined,
    tooltipText: undefined,
    icon: undefined,
  };
};

/**
 * A chip component that displays entity information with appropriate icons and tooltips.
 * Supports navigation to entity detail pages when the user has the required permissions.
 *
 * @param props - The component props
 * @param props.entityType - The type of entity (contract, order, purchase requisition, sourcing project, or supplier)
 * @param props.uuid - The unique identifier of the entity
 * @param props.url - Optional URL for external links; if provided, the chip will link to this URL instead of internal routing
 *
 * @returns A rendered chip component with entity information, or null if the namespace is not found
 *
 * @example
 * ```tsx
 * <BaseEntityChip
 *   entityType={ModuleEntityType.CONTRACT}
 *   uuid="123e4567-e89b-12d3-a456-426614174000"
 * />
 * ```
 *
 * @example2
 * ```tsx
 * <BaseEntityChip
 *   entityType={"contract"}
 *   uuid="123e4567-e89b-12d3-a456-426614174000"
 *   url="https://external-system.com/contracts/123e4567-e89b-12d3-a456-426614174000"
 * />
 * ```
 */
export const BaseEntityChip = ({ entityType, uuid, url, name }: BaseEntityChipProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'BaseEntityChip' });
  const { namespace, permission, routeId, icon, tooltipText } = mapEntityDetails(entityType, t);
  const isAllowed = useRestricted({
    permission,
  });

  if (!namespace) {
    return null;
  }

  const renderChip = (label: string) => {
    // If a URL is provided, render the chip as a link to the URL.
    // Permission checks are not required, because these URLs are external.
    if (url) {
      return (
        <Tooltip title={tooltipText} arrow>
          <Link
            to={url}
            target="_blank"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'underline' }}
          >
            {label}
            <ExternalLinkIcon size="16" fontSize="small" />
          </Link>
        </Tooltip>
      );
    }

    return isAllowed && routeId ? (
      <Tooltip title={tooltipText} arrow>
        <Link
          to={routeId(uuid)}
          target="_blank"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'underline' }}
        >
          {label}
          <ExternalLinkIcon size="16" fontSize="small" />
        </Link>
      </Tooltip>
    ) : (
      <Chip component="span" label={label} icon={icon} sx={{ maxWidth: 500 }} />
    );
  };

  if (name) {
    return renderChip(name);
  }

  return (
    <CuriosityValue ns={namespace} i18nKey={uuid} valueGetter={(ct) => ct}>
      {(value) => {
        let label = t('fallback.loading');
        if (value && typeof value !== 'string') {
          const ct = value as CuriosityTranslation;
          label = ct.value;
        }
        return renderChip(label);
      }}
    </CuriosityValue>
  );
};
